import { renderTwilightShiftAstrolabe } from './svg/twilightShiftSvg';

// -------------------------------------------------------------
// HYPER-PARAMETERS / CONFIG
// -------------------------------------------------------------
const CONFIG = {
    duration: 8000,
    genesisTime: 1200,
    spiralTime: 5500,
    collapseTime: 6700,
    astrolabeScaleMax: 1.8,
    focalLength: 500, // For 3D perspective projection
};

// -------------------------------------------------------------
// DYNAMIC DAY/NIGHT GRADIENT
// -------------------------------------------------------------
type ColorStop = [number, number, number];
const interpolateColor = (c1: ColorStop, c2: ColorStop, t: number): string => {
    const r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
    const g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
    const b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
    return `rgb(${r}, ${g}, ${b})`;
};

const DAWN_COLORS: ColorStop[] = [
    [25, 20, 60],   // Top
    [180, 80, 100], // Mid
    [255, 150, 80]  // Bot
];
const DUSK_COLORS: ColorStop[] = [
    [10, 5, 40],
    [80, 20, 80],
    [120, 30, 40]
];
const NIGHT_COLORS: ColorStop[] = [
    [0, 0, 10],
    [5, 5, 20],
    [5, 5, 20]
];

// -------------------------------------------------------------
// 3D MATH HELPERS
// -------------------------------------------------------------
interface Point3D {
    x: number;
    y: number;
    z: number;
}
interface Point2D {
    x: number;
    y: number;
    scale: number;
}

const project3D = (p: Point3D, cx: number, cy: number, distance: number = 0): Point2D => {
    // Camera distance pushing the entire scene into the screen
    const zPos = p.z + distance;
    const focalLength = CONFIG.focalLength;
    if (zPos < -focalLength) return { x: cx, y: cy, scale: 0.001 }; // behind camera

    const scale = focalLength / (focalLength + zPos);
    return {
        x: cx + p.x * scale,
        y: cy + p.y * scale,
        scale,
    };
};

class TrailParticle {
    p: Point3D;
    life: number;
    maxLife: number;
    color: string;

    constructor(p: Point3D, color: string) {
        this.p = { ...p };
        // Jitter starting position slightly for volume
        this.p.x += (Math.random() - 0.5) * 15;
        this.p.y += (Math.random() - 0.5) * 15;
        this.p.z += (Math.random() - 0.5) * 15;
        this.life = 1.0;
        this.maxLife = 20 + Math.random() * 20;
        this.color = color;
    }

    update() {
        this.life -= 1.0 / this.maxLife;
    }
}

class Planet3D {
    // Orbital parameters
    radius: number;
    startRadius: number;
    angle: number;
    orbitSpeed: number;
    tiltZ: number; // Rotates the orbital plane around Z axis
    tiltX: number; // Rotates around X axis

    // Physical specifics
    size: number;
    colorGrad: { base: string, c1: string, c2: string, c3: string };
    isStar: boolean;
    selfRotation: number;
    moons: { dist: number, speed: number, size: number, angle: number, color: string }[] = [];

    // Trail
    trail: TrailParticle[] = [];

    // Current transformed state for sorting
    currentPos: Point3D = { x: 0, y: 0, z: 0 };
    projected: Point2D = { x: 0, y: 0, scale: 0 };

    isCinematic: boolean;
    zOffset: number = 0;

    constructor(maxCanvasRadius: number, isCinematic: boolean = false) {
        this.radius = maxCanvasRadius * 0.5 + Math.random() * (maxCanvasRadius * 0.4);
        this.startRadius = this.radius;
        this.angle = Math.random() * Math.PI * 2;
        this.orbitSpeed = 0.002 + Math.random() * 0.004; // SLOWED DOWN drastically (was ~0.01+)
        this.isCinematic = isCinematic;

        // 3D orientation of the orbit
        this.tiltX = (Math.random() - 0.5) * Math.PI; // -90 to 90 deg tilt
        this.tiltZ = Math.random() * Math.PI * 2; // pan rotation

        if (this.isCinematic) {
            // Majestic flyby trajectory: start very close to the camera lens
            this.angle = Math.PI * 1.25; // Good sweeping angle
            this.zOffset = -450; // Focal length is 500, so this makes it incredibly huge
            this.orbitSpeed = 0.005; // Slightly faster to sweep past
            this.tiltX = 0.5; // Gentle tilt
        }

        this.size = 15 + Math.random() * 30;
        this.selfRotation = Math.random() * Math.PI * 2;

        const types = [
            { type: 'gas', glow: '#FF4500', colors: { c1: '#FF7F50', c2: '#FF4500', c3: '#8B0000' } },
            { type: 'water', glow: '#00FFFF', colors: { c1: '#00FFFF', c2: '#4169E1', c3: '#000080' } },
            { type: 'toxic', glow: '#32CD32', colors: { c1: '#ADFF2F', c2: '#32CD32', c3: '#006400' } },
            { type: 'star', glow: '#FFFFFF', colors: { c1: '#FFFFFF', c2: '#FFFFCC', c3: '#FFD700' } }, // Replaces crystal
            { type: 'volcanic', glow: '#FF0000', colors: { c1: '#FFA500', c2: '#FF0000', c3: '#330000' } },
            { type: 'ice', glow: '#E0FFFF', colors: { c1: '#F0FFFF', c2: '#AFEEEE', c3: '#5F9EA0' } },
            { type: 'shadow', glow: '#4B0082', colors: { c1: '#DDA0DD', c2: '#800080', c3: '#191970' } },
        ];

        const t = types[Math.floor(Math.random() * types.length)];
        this.colorGrad = { base: t.glow, ...t.colors };
        this.isStar = t.type === 'star';

        // Initialize Moons (skip stars)
        if (!this.isStar && Math.random() > 0.3) {
            const moonCount = 1 + Math.floor(Math.random() * 3);
            for (let i = 0; i < moonCount; i++) {
                this.moons.push({
                    dist: this.size * (1.5 + Math.random() * 2), // Orbit distance relative to size
                    size: this.size * (0.15 + Math.random() * 0.2), // Moon size
                    speed: (Math.random() > 0.5 ? 1 : -1) * (0.02 + Math.random() * 0.05), // Orbit speed
                    angle: Math.random() * Math.PI * 2,
                    color: ['#A0A0A0', '#D3D3D3', '#808080', '#EEDDCC'][Math.floor(Math.random() * 4)]
                });
            }
        }
    }

    update(timeProgress: number, t: number) {
        if (timeProgress < 0) return;

        // Spiraling in - Accelerating as it falls, but gently
        const easedProgress = Math.pow(timeProgress, 2); // Smoother curve

        // As timeProgress approaches 1, radius exponentially shrinks to 0. Scale also shrinks.
        this.radius = this.startRadius * (1 - easedProgress) - (timeProgress * 5);
        if (this.radius < 0) this.radius = 0;

        // Smoothly pull the cinematic planet back into the standard depth as we spiral
        if (this.isCinematic && this.zOffset < 0) {
            this.zOffset += 2.5; // Eases back into the distance
            if (this.zOffset > 0) this.zOffset = 0;
        }

        // Accelerate orbit gently (less frantic)
        const currentSpeed = this.orbitSpeed * (1 + easedProgress * 3); // Reduced acceleration multiplier from 15 to 3
        this.angle += currentSpeed;
        this.selfRotation -= currentSpeed * 0.5; // Slowed self rotation

        // Calculate raw un-tilted 3D position
        const rawX = this.radius * Math.cos(this.angle);
        const rawZ = this.radius * Math.sin(this.angle);
        const rawY = 0;

        // Apple tilt on X axis
        const tX = rawX;
        const tY = rawY * Math.cos(this.tiltX) - rawZ * Math.sin(this.tiltX);
        const tZ = rawY * Math.sin(this.tiltX) + rawZ * Math.cos(this.tiltX);

        // Apply pan on Z axis (effectively twisting the plane relative to camera)
        this.currentPos = {
            x: tX * Math.cos(this.tiltZ) - tY * Math.sin(this.tiltZ),
            y: tX * Math.sin(this.tiltZ) + tY * Math.cos(this.tiltZ),
            z: tZ + this.zOffset // Apply cinematic pull
        };

        // Add to trail (intermittent)
        if (Math.random() > 0.3 && this.radius > 20) {
            this.trail.push(new TrailParticle(this.currentPos, this.colorGrad.base));
        }

        // Update trails
        this.trail.forEach(pt => pt.update());
        this.trail = this.trail.filter(pt => pt.life > 0);

        // Update moons
        for (let m of this.moons) {
            m.angle += m.speed;
        }
    }

    drawTrail(ctx: CanvasRenderingContext2D, cx: number, cy: number, globalAlpha: number) {
        ctx.save();
        ctx.globalAlpha = globalAlpha;

        for (let pt of this.trail) {
            const proj = project3D(pt.p, cx, cy);
            if (proj.scale <= 0) continue;

            ctx.beginPath();
            const sz = 4 * proj.scale * pt.life; // Size fades over life
            ctx.arc(proj.x, proj.y, sz, 0, Math.PI * 2);
            ctx.fillStyle = pt.color;
            ctx.globalAlpha = globalAlpha * pt.life * 0.6;
            ctx.fill();
        }
        ctx.restore();
    }

    drawBody(ctx: CanvasRenderingContext2D, cx: number, cy: number, globalAlpha: number) {
        if (this.radius <= 5) return;

        this.projected = project3D(this.currentPos, cx, cy);
        if (this.projected.scale <= 0) return; // Behind camera plane

        const p = this.projected;

        // Shrink the planet as it gets sucked into the center singularity 
        // (radius determines distance to center, so if it's very close, it rapidly shrinks)
        let suckScale = 1.0;
        if (this.radius < 50) {
            suckScale = Math.max(0.01, this.radius / 50); // Approaching event horizon
        }

        const drawSize = this.size * p.scale * suckScale;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.globalAlpha = globalAlpha;

        const backMoons = this.moons.filter(m => Math.sin(m.angle) < 0);
        const frontMoons = this.moons.filter(m => Math.sin(m.angle) >= 0);

        const drawMoon = (m: any) => {
            const mx = Math.cos(m.angle) * m.dist * p.scale;
            const my = Math.sin(m.angle) * m.dist * p.scale * 0.3; // Give orbit some 3D squish
            ctx.beginPath();
            ctx.arc(mx, my, m.size * p.scale, 0, Math.PI * 2);
            ctx.fillStyle = m.color;
            ctx.fill();
        };

        // Draw back moons (behind planet)
        backMoons.forEach(drawMoon);

        if (this.isStar) {
            // Draw an intense, blistering star instead of a planet
            const coronaRadius = drawSize * 2.5;
            ctx.beginPath();
            ctx.arc(0, 0, coronaRadius, 0, Math.PI * 2);
            const corona = ctx.createRadialGradient(0, 0, drawSize * 0.5, 0, 0, coronaRadius);
            corona.addColorStop(0, 'rgba(255, 255, 255, 1)');
            corona.addColorStop(0.2, this.colorGrad.base); // use the glow color
            corona.addColorStop(1, 'rgba(0, 0, 0, 0)');
            ctx.fillStyle = corona;
            ctx.fill();

            // Core
            ctx.beginPath();
            ctx.arc(0, 0, drawSize * 0.8, 0, Math.PI * 2);
            ctx.fillStyle = '#FFFFFF';
            ctx.fill();

            // Lens flare / rays rotating slowly
            ctx.rotate(this.selfRotation * 0.2);
            for (let i = 0; i < 4; i++) {
                ctx.save();
                ctx.rotate(i * Math.PI / 4);
                ctx.beginPath();
                ctx.ellipse(0, 0, drawSize * 4, drawSize * 0.15, 0, 0, Math.PI * 2);
                ctx.fillStyle = `rgba(255, 255, 255, ${i % 2 === 0 ? 0.7 : 0.4})`;
                ctx.fill();
                ctx.restore();
            }
        } else {
            // Glow (coma) based on size
            ctx.beginPath();
            const glowRadius = drawSize * 1.8;
            ctx.arc(0, 0, glowRadius, 0, Math.PI * 2);
            const glow = ctx.createRadialGradient(0, 0, drawSize * 0.8, 0, 0, glowRadius);
            glow.addColorStop(0, `rgba(255, 255, 255, 0.4)`);
            glow.addColorStop(0.3, `rgba(200, 200, 255, 0.2)`);
            glow.addColorStop(1, 'rgba(0,0,0,0)');
            ctx.fillStyle = glow;
            ctx.fill();

            // Planet Body (rotated for its texture)
            ctx.save();
            ctx.rotate(this.selfRotation);

            ctx.beginPath();
            ctx.arc(0, 0, drawSize, 0, Math.PI * 2);

            // Atmosphere / base texture
            const grad = ctx.createLinearGradient(-drawSize, -drawSize, drawSize, drawSize);
            grad.addColorStop(0, this.colorGrad.c1);
            grad.addColorStop(0.5, this.colorGrad.c2);
            grad.addColorStop(1, this.colorGrad.c3);
            ctx.fillStyle = grad;
            ctx.fill();

            ctx.restore(); // unrotate to align shadow properly with screen space!

            // Dynamic 3D shading (terminator opposite origin/center light)
            const dx = p.x - cx;
            const dy = p.y - cy;
            const dist = Math.sqrt(dx * dx + dy * dy);

            let nx = 0, ny = 0;
            if (dist > 0) {
                nx = dx / dist;
                ny = dy / dist;
            }

            // Shadow grows from the far side depending on position
            ctx.beginPath();
            ctx.arc(0, 0, drawSize, 0, Math.PI * 2);

            // Shadow origin on the "dark" side
            const sx = nx * drawSize * 0.4;
            const sy = ny * drawSize * 0.4;

            const shadow = ctx.createRadialGradient(sx, sy, 0, 0, 0, drawSize * 1.2);
            shadow.addColorStop(0, 'rgba(0,0,0,0.85)');
            shadow.addColorStop(0.4, 'rgba(0,0,0,0.5)');
            shadow.addColorStop(1, 'rgba(255,255,255,0.2)'); // RIM LIGHT
            ctx.fillStyle = shadow;
            ctx.fill();
        }

        // Draw front moons
        frontMoons.forEach(drawMoon);

        ctx.restore();
    }
}

class ChromaticImplosion {
    radius: number = 0;
    maxRadius: number;
    alpha: number = 1;
    time: number = 0;
    shatterLines: { angle: number, length: number, speed: number, r: number, g: number, b: number }[] = [];

    constructor(maxRadius: number) {
        this.maxRadius = maxRadius;
        this.radius = 50; // Starts small at the core

        // Generate reality-shattering crack lines
        for (let i = 0; i < 30; i++) {
            this.shatterLines.push({
                angle: Math.random() * Math.PI * 2,
                length: 10 + Math.random() * 50,
                speed: 10 + Math.random() * 25,
                r: Math.random() > 0.5 ? 255 : 0,
                g: 255,
                b: Math.random() > 0.5 ? 255 : 0
            });
        }
    }

    update() {
        this.time += 1;
        // Supernova explosion outwards, not an implosion anymore
        this.radius += (this.maxRadius - this.radius) * 0.15 + 10;

        for (let line of this.shatterLines) {
            line.length += line.speed;
            line.speed *= 0.95; // friction
        }

        if (this.radius > this.maxRadius * 0.5) {
            this.alpha -= 0.04;
        }
    }

    draw(ctx: CanvasRenderingContext2D, cx: number, cy: number) {
        if (this.alpha <= 0) return;
        ctx.save();
        ctx.globalAlpha = Math.max(0, this.alpha);

        // Core blinding flash
        ctx.beginPath();
        ctx.arc(cx, cy, this.radius * 0.8, 0, Math.PI * 2);
        const flash = ctx.createRadialGradient(cx, cy, 0, cx, cy, this.radius * 0.8);
        flash.addColorStop(0, 'rgba(255, 255, 255, 1)');
        flash.addColorStop(0.2, `rgba(0, 255, 255, ${this.alpha})`);
        flash.addColorStop(0.6, `rgba(255, 0, 255, ${this.alpha * 0.5})`);
        flash.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.fillStyle = flash;
        ctx.fill();

        // Expanding Chromatic Shockwave Ring
        ctx.lineWidth = 20 * this.alpha;
        ctx.beginPath();
        ctx.arc(cx, cy, this.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 255, 255, ${this.alpha})`;
        ctx.stroke();

        ctx.lineWidth = 15 * this.alpha;
        ctx.beginPath();
        ctx.arc(cx, cy, this.radius * 1.05, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(0, 255, 255, ${this.alpha * 0.8})`;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(cx, cy, this.radius * 0.95, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 0, 255, ${this.alpha * 0.8})`;
        ctx.stroke();

        // Reality Shatter Lines (cracks in spacetime)
        for (let line of this.shatterLines) {
            ctx.save();
            ctx.translate(cx, cy);
            ctx.rotate(line.angle);

            ctx.beginPath();
            ctx.moveTo(this.radius * 0.2, 0);
            ctx.lineTo(this.radius * 0.2 + line.length, 0);

            // Randomly jagged spacetime cracks
            ctx.lineTo(this.radius * 0.2 + line.length * 0.8, line.length * 0.1);

            ctx.strokeStyle = `rgba(${line.r}, ${line.g}, ${line.b}, ${this.alpha})`;
            ctx.lineWidth = 3 + Math.random() * 4;
            ctx.stroke();
            ctx.restore();
        }

        ctx.restore();
    }
}

export type TwilightShiftEffect = {
    kind: 'twilightShift';
    t: number;
    sx: number;
    sy: number;
    data?: {
        planets: Planet3D[];
        implosion: ChromaticImplosion | null;
        astrolabeRot: number;
        canvasRadius: number;
        absorbedAlpha: number;
        stars: { x: number, y: number, z: number, r: number, a: number }[];
    };
};

export const createTwilightShiftEffect = (sx: number, sy: number): TwilightShiftEffect => ({
    kind: 'twilightShift',
    t: 0,
    sx,
    sy,
});

export const renderTwilightShiftEffect = (
    eff: TwilightShiftEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    const { width, height } = ctx.canvas;
    const cx = width / 2;
    const cy = height / 2;

    if (!eff.data) {
        eff.data = {
            planets: [],
            implosion: null,
            astrolabeRot: 0,
            canvasRadius: Math.sqrt(cx * cx + cy * cy),
            absorbedAlpha: 0,
            stars: []
        };
        // Seed 9 planets for dense magnificent system
        for (let i = 0; i < 9; i++) {
            eff.data.planets.push(new Planet3D(eff.data.canvasRadius, i === 0)); // Index 0 gets the cinematic flyby
        }

        // Seed some parallax stars
        for (let i = 0; i < 150; i++) {
            eff.data.stars.push({
                x: (Math.random() - 0.5) * 2000,
                y: (Math.random() - 0.5) * 2000,
                z: Math.random() * 2000 + 500, // Deep background
                r: Math.random() * 2 + 0.5,
                a: Math.random()
            });
        }
    }

    const d = eff.data;

    if (eff.t > CONFIG.duration && (!d.implosion || d.implosion.alpha <= 0)) {
        return false;
    }

    ctx.save();

    // -----------------------------------------------------------------
    // 1. DYNAMIC GRADIENT BACKGROUND (Dawn -> Dusk -> Night)
    // -----------------------------------------------------------------
    if (eff.t >= CONFIG.genesisTime && eff.t <= CONFIG.collapseTime + 500) {
        const cycleProgress = Math.min(1, Math.max(0, (eff.t - CONFIG.genesisTime) / (CONFIG.collapseTime - CONFIG.genesisTime)));

        // Master alpha curve to fade in the atmosphere and fade it out
        let intensityMaster = 1;
        if (cycleProgress < 0.15) intensityMaster = cycleProgress / 0.15; // Smooth 15% fade in
        if (eff.t > CONFIG.collapseTime) {
            intensityMaster = Math.max(0, 1 - (eff.t - CONFIG.collapseTime) / 500); // 500ms fade out after collapse
        }

        let cTop, cMid, cBot;
        if (cycleProgress < 0.5) {
            // Dawn to Dusk
            const p = cycleProgress * 2; // 0 to 1
            cTop = interpolateColor(DAWN_COLORS[0], DUSK_COLORS[0], p);
            cMid = interpolateColor(DAWN_COLORS[1], DUSK_COLORS[1], p);
            cBot = interpolateColor(DAWN_COLORS[2], DUSK_COLORS[2], p);
        } else {
            // Dusk to Night
            const p = (cycleProgress - 0.5) * 2; // 0 to 1
            cTop = interpolateColor(DUSK_COLORS[0], NIGHT_COLORS[0], p);
            cMid = interpolateColor(DUSK_COLORS[1], NIGHT_COLORS[1], p);
            cBot = interpolateColor(DUSK_COLORS[2], NIGHT_COLORS[2], p);
        }

        const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
        bgGrad.addColorStop(0, cTop);
        bgGrad.addColorStop(0.5, cMid);
        bgGrad.addColorStop(1, cBot);

        ctx.globalCompositeOperation = 'source-over';
        ctx.globalAlpha = 0.8 * intensityMaster; // Blend with existing canvas
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);

        // Draw Starfield (fades in as night approaches)
        const starAlphaBase = Math.max(0, (cycleProgress - 0.4) / 0.6); // Starts past dusk
        if (starAlphaBase > 0) {
            ctx.globalAlpha = starAlphaBase * intensityMaster;
            for (let s of d.stars) {
                const p = project3D({ x: s.x, y: s.y, z: s.z }, cx, cy);
                if (p.scale > 0) {
                    // Star twinkle
                    s.a += (Math.random() - 0.5) * 0.1;
                    if (s.a > 1) s.a = 1; if (s.a < 0) s.a = 0;

                    ctx.beginPath();
                    ctx.arc(p.x, p.y, s.r * p.scale, 0, Math.PI * 2);
                    ctx.fillStyle = `rgba(255, 255, 255, ${s.a})`;
                    ctx.fill();
                }
            }
        }
    }
    ctx.globalAlpha = 1;

    // -----------------------------------------------------------------
    // 2. THE 3D PLANET SPIRAL
    // -----------------------------------------------------------------
    let planetsAlive = 0;
    if (eff.t >= CONFIG.genesisTime) {
        const spiralProgress = Math.max(0, (eff.t - CONFIG.genesisTime) / (CONFIG.collapseTime - CONFIG.genesisTime));

        // Update physics
        d.planets.forEach(p => p.update(spiralProgress, eff.t));

        // Z-SORTING: Furthest (highest Z) drawn first
        const sortedPlanets = [...d.planets].sort((a, b) => b.currentPos.z - a.currentPos.z);

        // Draw Trails First (Background layer relative to planets)
        sortedPlanets.forEach(p => p.drawTrail(ctx, cx, cy, 1));

        // Draw Bodies
        sortedPlanets.forEach(p => {
            p.drawBody(ctx, cx, cy, 1);
            if (p.radius > 5) planetsAlive++;
        });

        if (spiralProgress > 0.8 && planetsAlive < d.planets.length) {
            d.absorbedAlpha = Math.min(1, d.absorbedAlpha + 0.1);
        }
    }

    // -----------------------------------------------------------------
    // 3. CHROMATIC IMPLOSION (Post-Collapse)
    // -----------------------------------------------------------------
    if (eff.t >= CONFIG.collapseTime && !d.implosion) {
        d.implosion = new ChromaticImplosion(d.canvasRadius + 400); // Massive spawn
        d.absorbedAlpha = 1;
    }

    if (d.implosion) {
        d.implosion.update();
        d.implosion.draw(ctx, cx, cy);
    }

    // -----------------------------------------------------------------
    // 4. THE ASTROLABE (Centerpiece)
    // -----------------------------------------------------------------
    if (eff.t < CONFIG.collapseTime + 500) {
        let astrolabeScale = 0;
        let glow = 0;

        if (eff.t < CONFIG.genesisTime) {
            const p = eff.t / CONFIG.genesisTime;
            // Eased spawn (Overshoot)
            const easeOutBack = 1 + 2.70158 * Math.pow(p - 1, 3) + 1.70158 * Math.pow(p - 1, 2);
            astrolabeScale = CONFIG.astrolabeScaleMax * easeOutBack;
            if (astrolabeScale < 0) astrolabeScale = 0;

            d.astrolabeRot += 0.05;
            glow = p;
        } else if (eff.t >= CONFIG.genesisTime && eff.t < CONFIG.collapseTime) {
            const spiralProgress = (eff.t - CONFIG.genesisTime) / (CONFIG.collapseTime - CONFIG.genesisTime);
            astrolabeScale = CONFIG.astrolabeScaleMax;
            d.astrolabeRot += 0.01 + (spiralProgress * 0.05); // Very slow, majestic rotation
            // Gets exponentially brighter as planets hit
            glow = 1.0 + Math.pow(d.absorbedAlpha, 2) * 8;
        } else {
            // Imploding Astrolabe (Pushed super fast)
            const p = (eff.t - CONFIG.collapseTime) / 500;
            astrolabeScale = CONFIG.astrolabeScaleMax * (1 - p);
            if (astrolabeScale < 0) astrolabeScale = 0;
            d.astrolabeRot -= 0.2; // Smooth reverse spin
            glow = 1.0 + (1 - p) * 12;
        }

        if (astrolabeScale > 0) {
            const timeParam = eff.t * 0.005;

            // Singularity Burst Backlight (Magenta / Cyan themed instead of pure white)
            if (d.absorbedAlpha > 0) {
                ctx.beginPath();
                ctx.arc(cx, cy, 200 * d.absorbedAlpha, 0, Math.PI * 2);
                const bgGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, 200 * d.absorbedAlpha);
                bgGlow.addColorStop(0, `rgba(255, 255, 255, ${d.absorbedAlpha})`);
                bgGlow.addColorStop(0.3, `rgba(0, 255, 255, ${d.absorbedAlpha * 0.7})`);
                bgGlow.addColorStop(0.7, `rgba(255, 0, 255, ${d.absorbedAlpha * 0.4})`);
                bgGlow.addColorStop(1, 'rgba(0,0,0,0)');
                ctx.fillStyle = bgGlow;
                ctx.fill();
            }

            renderTwilightShiftAstrolabe(
                ctx,
                cx,
                cy,
                astrolabeScale,
                d.astrolabeRot,
                Math.min(1, astrolabeScale * 1.5),
                glow,
                timeParam
            );
        }
    }

    ctx.restore();
    return true;
};
